import React from "react";
import { useShallow } from "zustand/react/shallow";
import { usePanelStore } from "../stores/panelStore";
import { useViewerStore, getViewerActions } from "../stores/viewerStore";
import { useSelectionStore } from "../stores/selectionStore";
import { useFileStore } from "../stores/fileStore";
import { makeHistorySnapshot, getHistoryActions } from "../stores/historyStore";
import { getSceneState, getSceneActions, useSceneStore } from "../stores/sceneStore";
import {
  getProjectSaveActions,
  useLastSavedSnapshotHash,
  useLastSaveTarget,
  useProjectSaveStatus,
} from "../stores/projectSaveStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { startSimulationFromCurrentFiles, stopSimulation } from "../physics/mujocoBridge";
import { getSceneBehaviorContextStoreActions } from "../stores/sceneBehaviorContextStore";
import { loadXmlCatalogEntry } from "../utils/xmlSourceLoader";
import { SHORTCUT_ACTIONS } from "../shortcuts/defaultBindings";
import { useShortcutAction } from "../shortcuts/useShortcutAction";
import { useSwipeToClose, useSwipeToOpen } from "../hooks/useSwipeToClose";
import { useMobileEdgeChromeSuppressed } from "../stores/mobileEdgeChromeStore";
import MobilePinnedToolPanelTab from "./MobilePinnedToolPanelTab";
import {
  isPhysicsAuthoringLocked,
  isPhysicsRuntimeAuthoringLocked,
  isReadOnlyProjectMode,
  setReadOnlyProjectMode,
} from "../utils/physicsGuards";
import { logger } from "../utils/logger";
import { loadEnvironmentZip } from "../utils/environmentBundle";
import {
  CloudApiError,
  getPublicShareLink,
  getPublicShareLinkVersionDetail,
  patchProject,
} from "../cloud/api";
import {
  loadProjectFromCloud,
  loadProjectFromVersionDetail,
  syncProjectToCloud,
} from "../cloud/sync";
import { useCurrentUser } from "../cloud/auth";
import { buildProjectSaveSnapshotHash } from "../utils/projectSaveSnapshot";
import { PROJECT_SAVE_REQUEST_EVENT } from "../utils/projectSaveRequests";
import {
  rememberDiscardedSceneForRestore,
  RESTORE_DISCARDED_SCENE_EVENT,
} from "../utils/discardedSceneRecovery";
import {
  hasMeaningfulSceneWork,
  requestSceneReplacementConfirmation,
} from "../utils/sceneReplacementGuards";
import { showBrowserImportLimitIssue } from "../utils/importLimits";
import { prepareFilesForUrdfImport } from "../utils/urdfImportChoice";
import MainPanelHeader from "./main-panel/MainPanelHeader";
import { MainPanelMobilePhysicsOverlay } from "./main-panel/MainPanelPhysicsControls";
import MainPanelExportModals from "./main-panel/MainPanelExportModals";
import MainPanelToolPanels from "./main-panel/MainPanelToolPanels";
import MainPanelCollapsedTab from "./main-panel/MainPanelCollapsedTab";
import MainPanelContent from "./main-panel/MainPanelContent";
import MainPanelHiddenFileInputs from "./main-panel/MainPanelHiddenFileInputs";
import CloudProjectsPanel from "./draggable-panels/CloudProjectsPanel";
import {
  DesktopSceneLoadToast,
  MobileSceneLoadOverlay,
} from "./draggable-panels/object-library/MobileLibraryOverlays";
import { isInteractiveShortcutTarget, useIsMobilePanelViewport } from "./main-panel/mainPanelHooks";
import { useMainPanelOpenControls } from "./main-panel/useMainPanelOpenControls";
import { useMainPanelEventSubscriptions } from "./main-panel/useMainPanelEventSubscriptions";
import { useMainPanelPanelState } from "./main-panel/useMainPanelPanelState";
import { usePinnedToolPanelTabConfig } from "./main-panel/usePinnedToolPanelTabConfig";
import {
  MAIN_MENU_PANEL_MAX_WIDTH_DEFAULT,
  MAIN_MENU_PANEL_MAX_WIDTH_MOBILE,
  MAIN_MENU_PANEL_TOGGLE_OPEN_CLASS,
} from "./main-panel/constants";
import { useMainPanelExports } from "./main-panel/useMainPanelExports";
import { suggestExportProjectName } from "./main-panel/exportUtils";

const isXmlFile = (file) => /\.(xml|urdf|xacro)$/i.test(String(file?.name || ""));

function shouldShowRuntimeDiagnostics() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("runtimeDiagnostics") === "1";
}

const hasFileStoreSceneWork = () => {
  const state = useFileStore.getState();
  return Boolean(
    state.xmlFile ||
    Object.keys(state.fileMap || {}).length > 0 ||
    Object.keys(state.keyframes || {}).length > 0 ||
    state.currentLoadingFolder
  );
};

const getCurrentProjectSaveSnapshotHash = () =>
  buildProjectSaveSnapshotHash({
    scene: getSceneState(),
    fileState: useFileStore.getState(),
  });

const isResolvingMissingUpload = () => {
  const fileState = useFileStore.getState();
  return (
    Array.isArray(fileState.missingFiles) &&
    fileState.missingFiles.length > 0 &&
    Boolean(fileState.xmlFile)
  );
};

const showMainPanelToast = (message, options = {}) => {
  window.dispatchEvent(new CustomEvent("critter-toast", { detail: { message, ...options } }));
};

const delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const SHARED_PROJECT_NEW_PROJECT_EVENT = "shared-project-new-project";
const CLOUD_PROJECT_VERSION_CONFLICT_EVENT = "cloud-project-version-conflict";
const CLOUD_AUTOSAVE_DEBOUNCE_MS = 15_000;
const CLOUD_AUTOSAVE_MAX_UNSAVED_MS = 120_000;
const CLOUD_AUTOSAVE_MIN_INTERVAL_MS = 120_000;
const CLOUD_AUTOSAVE_PROJECT_NAME_FALLBACK_PREFIX = "Autosaved project";

const isPlaceholderProjectName = (name) => {
  const normalized = String(name || "")
    .trim()
    .toLowerCase();
  return (
    !normalized ||
    normalized === "untitled scene" ||
    normalized === "new project" ||
    normalized === "blank scene"
  );
};

const autosaveFallbackProjectName = () =>
  `${CLOUD_AUTOSAVE_PROJECT_NAME_FALLBACK_PREFIX} - ${new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date())}`;

const autosaveProjectName = (exportProjectName, fileProjectName) => {
  if (!isPlaceholderProjectName(exportProjectName)) return exportProjectName;
  if (!isPlaceholderProjectName(fileProjectName)) return fileProjectName;

  const sceneSuggestedName = suggestExportProjectName(fileProjectName, getSceneState());
  if (!isPlaceholderProjectName(sceneSuggestedName)) return sceneSuggestedName;

  return autosaveFallbackProjectName();
};

const shareTokenFromLocation = () => {
  if (typeof window === "undefined") return "";
  const match = window.location.pathname.match(/^\/share\/([^/?#]+)/);
  if (!match?.[1]) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const cloudErrorMessage = (error) => {
  if (error instanceof CloudApiError) {
    const body = error.body;
    if (body && typeof body === "object" && body.error === "version_conflict") {
      return "This cloud project changed in another tab or device. Reload the latest version before saving again.";
    }
    if (body && typeof body === "object" && body.error === "quota_exceeded") {
      const limit = Number.isFinite(body.limit) ? ` Limit: ${body.limit}.` : "";
      const requested = Number.isFinite(body.requested) ? ` Requested: ${body.requested}.` : "";
      return `Cloud quota exceeded.${limit}${requested}`;
    }
  }
  return error instanceof Error ? error.message : "Cloud sync failed.";
};

const isCloudVersionConflictError = (error) =>
  error instanceof CloudApiError &&
  error.status === 409 &&
  error.body &&
  typeof error.body === "object" &&
  error.body.error === "version_conflict";

const ModernMainPanel = ({ position = {} }) => {
  // Store state
  const showPerformancePanel = useViewerStore((state) => state.showPerformancePanel);
  const { actions: fileActions } = useFileStore.getState();
  const selectEquality = useSelectionStore((state) => state.selectEquality);
  const selectTendon = useSelectionStore((state) => state.selectTendon);
  const setLastSelectionSource = useSelectionStore((state) => state.setLastSelectionSource);

  // Global panel state
  const {
    sceneGraphOpen,
    setSceneGraphOpen,
    filePanelOpen,
    setFilePanelOpen,
    animationPanelOpen,
    setAnimationPanelOpen,
    materialEditorPanelOpen,
    setMaterialEditorPanelOpen,
    lightsPanelOpen,
    setLightsPanelOpen,
    circleDetectionOpen,
    setCircleDetectionOpen,
    historyPanelOpen,
    setHistoryPanelOpen,
    runtimeDebugOpen,
    setRuntimeDebugOpen,
    ikTargetPanelOpen,
    setIkTargetPanelOpen,
    objectLibraryOpen,
    setObjectLibraryOpen,
    runtimeControlsOpen,
    setRuntimeControlsOpen,
    physicsPanelOpen,
    setPhysicsPanelOpen,
    actuatorControlsOpen,
    setActuatorControlsOpen,
    warmstartDatasetOpen,
    setWarmstartDatasetOpen,
    behaviorEditorOpen,
    setBehaviorEditorOpen,
    policyRolloutOpen,
    setPolicyRolloutOpen,
    trainingMetricsOpen,
    setTrainingMetricsOpen,
    trainingPipelineOpen,
    setTrainingPipelineOpen,
    octoTrainingOpen,
    setOctoTrainingOpen,
    meshEditorOpen,
    setMeshEditorOpen,
    sceneInventoryOpen,
    setSceneInventoryOpen,
    modelCheckOpen,
    setModelCheckOpen,
    massPropertiesOpen,
    setMassPropertiesOpen,
    measurementsOpen,
    setMeasurementsOpen,
    setVisibilitySettingsOpen,
    terrainPanelOpen,
    setTerrainPanelOpen,
    xmlSourcePanelOpen,
    setXmlSourcePanelOpen,
    cameraProbeOpen,
    setCameraProbeOpen,
    sensorsOpen,
    setSensorsOpen,
    shortcutsPanelOpen,
    setShortcutsPanelOpen,
    getUpdatesPanelOpen,
    setGetUpdatesPanelOpen,
    sendFeedbackPanelOpen,
    setSendFeedbackPanelOpen,
    setAboutPanelOpen,
    setGremlinsPanelOpen,
  } = useMainPanelPanelState();

  const [policyRolloutRequest, setPolicyRolloutRequest] = React.useState(null);
  const [motorGain, setMotorGain] = React.useState(1);
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [loadingXmlSourceId, setLoadingXmlSourceId] = React.useState("");
  const [selectedXmlSourceId, setSelectedXmlSourceId] = React.useState("");
  const [propertiesPanelOpen, setPropertiesPanelOpen] = React.useState(false);
  const [aboutPanelMode, setAboutPanelMode] = React.useState("about");
  const [cloudProjectsOpen, setCloudProjectsOpen] = React.useState(false);
  const [cloudProjectId, setCloudProjectId] = React.useState(null);
  const [cloudVersionId, setCloudVersionId] = React.useState(null);
  const [cloudProjectName, setCloudProjectName] = React.useState(null);
  const [cloudProjectAccessRole, setCloudProjectAccessRole] = React.useState(null);
  const [cloudShareToken, setCloudShareToken] = React.useState(null);
  const [cloudProjectsRefreshKey, setCloudProjectsRefreshKey] = React.useState(0);
  const [cloudActionPending, setCloudActionPending] = React.useState(false);
  const [cloudProgressOpen, setCloudProgressOpen] = React.useState(false);
  const [cloudProgressPct, setCloudProgressPct] = React.useState(0);
  const [cloudProgressLabel, setCloudProgressLabel] = React.useState("cloud project");
  const [cloudProgressVerb, setCloudProgressVerb] = React.useState("Loading");
  const [cloudVersionConflict, setCloudVersionConflict] = React.useState(false);
  const cloudAutosavePendingRef = React.useRef(false);
  const cloudAutosaveTimerRef = React.useRef(null);
  const cloudAutosaveDirtySinceRef = React.useRef(null);
  const cloudAutosaveLastCloudSavedAtRef = React.useRef(null);
  const discardedCloudProjectSnapshotRef = React.useRef(null);
  const sharedProjectLoadStartedRef = React.useRef(false);
  const { isLoaded: cloudAuthLoaded, isSignedIn: cloudAuthSignedIn } = useCurrentUser();
  const projectSaveStatus = useProjectSaveStatus();
  const lastSaveTarget = useLastSaveTarget();
  const lastSavedSnapshotHash = useLastSavedSnapshotHash();
  const sceneSnapshotState = useSceneStore((state) => state);
  const projectSaveFileState = useFileStore(
    useShallow((state) => ({
      projectName: state.projectName,
      referencedFiles: state.referencedFiles,
      keyframes: state.keyframes,
      keyframeDetails: state.keyframeDetails,
      preservedMjcfSections: state.preservedMjcfSections,
    }))
  );
  const isMobilePanelViewport = useIsMobilePanelViewport();
  const showDevTools = import.meta.env.VITE_SHOW_DEV_TOOLS === "true";
  const showRuntimeDiagnostics = showDevTools || shouldShowRuntimeDiagnostics();
  const showTrainingIkTools =
    import.meta.env.VITE_SHOW_TRAINING_IK_TOOLS === "true" || showDevTools;

  const currentProjectSaveSnapshotHash = React.useMemo(() => {
    const { actions: _actions, ...scene } = sceneSnapshotState;
    return buildProjectSaveSnapshotHash({
      scene,
      fileState: projectSaveFileState,
    });
  }, [projectSaveFileState, sceneSnapshotState]);

  const cloudSaveMatchesCurrent =
    projectSaveStatus === "clean" &&
    lastSaveTarget === "cloud" &&
    Boolean(lastSavedSnapshotHash) &&
    currentProjectSaveSnapshotHash === lastSavedSnapshotHash;

  /** Open state adds persistent gray (see `main-menu-panel-toggle-open` in index.css); eye icon is additional affordance. */
  const getPanelToggleButtonClass = React.useCallback((isOpen, widthClass = "w-full") => {
    const open = isOpen ? ` ${MAIN_MENU_PANEL_TOGGLE_OPEN_CLASS}` : "";
    return `menu-button${open} ${widthClass} flex items-center gap-2 justify-start border rounded-md px-3 py-2 select-none focus:outline-none border-gray-700 bg-transparent text-gray-200`;
  }, []);
  const toggleShowPerformancePanel = React.useCallback(() => {
    getViewerActions().toggleShowPerformancePanel();
  }, []);

  React.useEffect(() => {
    if (showRuntimeDiagnostics && !showDevTools) {
      setRuntimeDebugOpen(true);
    }
  }, [showRuntimeDiagnostics, showDevTools, setRuntimeDebugOpen]);

  const handleXmlSourceSelect = (entry) => {
    if (!entry?.key || loadingXmlSourceId) return;
    setSelectedXmlSourceId(entry.key);
    setLoadingXmlSourceId(entry.key);
    void loadXmlCatalogEntry(entry)
      .then(() => {
        getSceneBehaviorContextStoreActions().clear();
      })
      .finally(() => {
        setLoadingXmlSourceId("");
      });
  };

  const closeMainPanelPopups = React.useCallback(() => {
    setSceneGraphOpen(false);
    setFilePanelOpen(false);
    setAnimationPanelOpen(false);
    setMaterialEditorPanelOpen(false);
    setLightsPanelOpen(false);
    setCircleDetectionOpen(false);
    setHistoryPanelOpen(false);
    setRuntimeDebugOpen(false);
    setIkTargetPanelOpen(false);
    setObjectLibraryOpen(false);
    setRuntimeControlsOpen(false);
    setPhysicsPanelOpen(false);
    setActuatorControlsOpen(false);
    setWarmstartDatasetOpen(false);
    setBehaviorEditorOpen(false);
    setPolicyRolloutOpen(false);
    setTrainingMetricsOpen(false);
    setTrainingPipelineOpen(false);
    setOctoTrainingOpen(false);
    setMeshEditorOpen(false);
    setSceneInventoryOpen(false);
    setModelCheckOpen(false);
    setMassPropertiesOpen(false);
    setMeasurementsOpen(false);
    setVisibilitySettingsOpen(false);
    setTerrainPanelOpen(false);
    setXmlSourcePanelOpen(false);
    setCameraProbeOpen(false);
    setSensorsOpen(false);
    setShortcutsPanelOpen(false);
    setGetUpdatesPanelOpen(false);
    setSendFeedbackPanelOpen(false);
    setAboutPanelOpen(false);
    setGremlinsPanelOpen(false);
    setCloudProjectsOpen(false);
  }, [
    setAnimationPanelOpen,
    setCircleDetectionOpen,
    setAboutPanelOpen,
    setActuatorControlsOpen,
    setBehaviorEditorOpen,
    setCameraProbeOpen,
    setFilePanelOpen,
    setGetUpdatesPanelOpen,
    setGremlinsPanelOpen,
    setHistoryPanelOpen,
    setIkTargetPanelOpen,
    setMaterialEditorPanelOpen,
    setLightsPanelOpen,
    setMeshEditorOpen,
    setObjectLibraryOpen,
    setOctoTrainingOpen,
    setPhysicsPanelOpen,
    setRuntimeControlsOpen,
    setPolicyRolloutOpen,
    setRuntimeDebugOpen,
    setSceneGraphOpen,
    setSceneInventoryOpen,
    setModelCheckOpen,
    setMassPropertiesOpen,
    setMeasurementsOpen,
    setSendFeedbackPanelOpen,
    setSensorsOpen,
    setShortcutsPanelOpen,
    setTerrainPanelOpen,
    setTrainingMetricsOpen,
    setTrainingPipelineOpen,
    setVisibilitySettingsOpen,
    setWarmstartDatasetOpen,
    setXmlSourcePanelOpen,
    setCloudProjectsOpen,
  ]);

  const {
    setSceneGraphOpenFromMenu,
    setFilePanelOpenFromMenu,
    setAnimationPanelOpenFromMenu,
    setMaterialEditorPanelOpenFromMenu,
    setLightsPanelOpenFromMenu,
    setCircleDetectionOpenFromMenu,
    setHistoryPanelOpenFromMenu,
    setRuntimeDebugOpenFromMenu,
    setMeasurementsOpenFromMenu,
    setIkTargetPanelOpenFromMenu,
    setObjectLibraryOpenFromMenu,
    setActuatorControlsOpenFromMenu,
    setBehaviorEditorOpenFromMenu,
    setTrainingPipelineOpenFromMenu,
    setOctoTrainingOpenFromMenu,
    setTrainingMetricsOpenFromMenu,
    setCameraProbeOpenFromMenu,
    setSensorsOpenFromMenu,
    setMeshEditorOpenFromMenu,
    setSceneInventoryOpenFromMenu,
    setModelCheckOpenFromMenu,
    setMassPropertiesOpenFromMenu,
    setVisibilitySettingsOpenFromMenu,
    setTerrainPanelOpenFromMenu,
    setXmlSourcePanelOpenFromMenu,
    setShortcutsPanelOpenFromMenu,
    setGetUpdatesPanelOpenFromMenu,
    setSendFeedbackPanelOpenFromMenu,
    setAboutPanelOpenFromMenu,
    openPhysicsPanel,
    openRuntimeControlsPanel,
    openTrainingMetricsPanel,
    openPolicyRolloutPanel,
    openWarmstartDatasetPanel,
    openOctoTrainingPanel,
    closeObjectLibraryPanel,
    closeSceneGraph,
    closeFilePanel,
    closeAnimationPanel,
    closeMaterialEditorPanel,
    closeLightsPanel,
    closeIkTargetPanel,
    closePhysicsPanel,
    closeRuntimeControlsPanel,
    closeActuatorControlsPanel,
    closeWarmstartDatasetPanel,
    closeBehaviorEditorPanel,
    closeTrainingPipelinePanel,
    closeOctoTrainingPanel,
    closeTrainingMetricsPanel,
    closePolicyRolloutPanel,
    closeCameraProbePanel,
    closeSensorsPanel,
    closeShortcutsPanel,
    closeCircleDetectionPanel,
    closeHistoryPanel,
    closeXmlSourcePanel,
    closeRuntimeDebugPanel,
    closeMeasurementsPanel,
    closeMeshEditorPanel,
    closeSceneInventory,
    closeModelCheckPanel,
    closeMassPropertiesPanel,
    closeTerrainPanel,
  } = useMainPanelOpenControls({
    isMobilePanelViewport,
    closeMainPanelPopups,
    setSceneGraphOpen,
    setFilePanelOpen,
    setAnimationPanelOpen,
    setMaterialEditorPanelOpen,
    setLightsPanelOpen,
    setCircleDetectionOpen,
    setHistoryPanelOpen,
    setRuntimeDebugOpen,
    setMeasurementsOpen,
    setIkTargetPanelOpen,
    setObjectLibraryOpen,
    setRuntimeControlsOpen,
    setPhysicsPanelOpen,
    setActuatorControlsOpen,
    setWarmstartDatasetOpen,
    setBehaviorEditorOpen,
    setPolicyRolloutOpen,
    setPolicyRolloutRequest,
    setTrainingMetricsOpen,
    setTrainingPipelineOpen,
    setOctoTrainingOpen,
    setMeshEditorOpen,
    setSceneInventoryOpen,
    setModelCheckOpen,
    setMassPropertiesOpen,
    setVisibilitySettingsOpen,
    setTerrainPanelOpen,
    setXmlSourcePanelOpen,
    setCameraProbeOpen,
    setSensorsOpen,
    setShortcutsPanelOpen,
    setGetUpdatesPanelOpen,
    setSendFeedbackPanelOpen,
    setAboutPanelOpen,
    setGremlinsPanelOpen,
  });

  const {
    pendingDeploymentBundle,
    pendingMjcfExport,
    includeMjcfDesktopScene,
    setIncludeMjcfDesktopScene,
    setPendingMjcfLiftModelToFloor,
    pendingEnvironmentExport,
    pendingKeyframeMismatchExport,
    quickProjectSavePending,
    exportProjectName,
    setExportProjectName,
    refreshSuggestedExportProjectName,
    handleExportMjcfXml,
    handleExportEnvironmentZip,
    handleQuickDownloadEnvironmentBundle,
    handleExportDeploymentBundle,
    handleDownloadPendingDeploymentBundle,
    handleCancelPendingDeploymentBundle,
    handleDownloadPendingEnvironmentBundle,
    handleDownloadPendingEnvironmentLightBundle,
    handleCancelPendingEnvironmentExport,
    handleDownloadPendingMjcfExport,
    handleDownloadPendingMjcfBundle,
    handleCancelPendingMjcfExport,
    handleSkipMismatchedKeyframes,
    handleAbortMismatchedKeyframes,
    handleExportReviewFeedback,
    handleExportReviewUpdates,
  } = useMainPanelExports({
    onOpenFeedbackPanel: () => setSendFeedbackPanelOpenFromMenu(true),
    onOpenUpdatesPanel: () => setGetUpdatesPanelOpenFromMenu(true),
  });

  const handleCollapse = React.useCallback(() => {
    setIsCollapsed(true);
  }, []);
  const handleExpand = React.useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const handleBodyReorder = React.useCallback((newItems) => {
    // This would integrate with your existing reorder logic
    logger.debug("Reorder bodies:", newItems);
    // You could add a setBodies action to your robot store
    // useSceneStore.getState().setBodies(newItems);
  }, []);

  const rememberCurrentSceneForRestore = React.useCallback(() => {
    discardedCloudProjectSnapshotRef.current = {
      cloudProjectId,
      cloudVersionId,
      cloudProjectName,
      cloudProjectAccessRole,
      cloudShareToken,
      cloudVersionConflict,
    };
    rememberDiscardedSceneForRestore();
  }, [
    cloudProjectAccessRole,
    cloudProjectId,
    cloudProjectName,
    cloudShareToken,
    cloudVersionConflict,
    cloudVersionId,
  ]);

  React.useEffect(() => {
    const restoreCloudProjectSnapshot = () => {
      const snapshot = discardedCloudProjectSnapshotRef.current;
      discardedCloudProjectSnapshotRef.current = null;
      if (!snapshot) return;
      setCloudProjectId(snapshot.cloudProjectId);
      setCloudVersionId(snapshot.cloudVersionId);
      setCloudProjectName(snapshot.cloudProjectName);
      setCloudProjectAccessRole(snapshot.cloudProjectAccessRole);
      setCloudShareToken(snapshot.cloudShareToken);
      setCloudVersionConflict(snapshot.cloudVersionConflict);
    };
    window.addEventListener(RESTORE_DISCARDED_SCENE_EVENT, restoreCloudProjectSnapshot);
    return () =>
      window.removeEventListener(RESTORE_DISCARDED_SCENE_EVENT, restoreCloudProjectSnapshot);
  }, []);

  const handleNewProject = React.useCallback(async () => {
    if (isPhysicsRuntimeAuthoringLocked()) return;
    try {
      rememberCurrentSceneForRestore();
      if (cloudAutosaveTimerRef.current) {
        window.clearTimeout(cloudAutosaveTimerRef.current);
        cloudAutosaveTimerRef.current = null;
      }
      cloudAutosaveDirtySinceRef.current = null;
      cloudAutosaveLastCloudSavedAtRef.current = null;
      setReadOnlyProjectMode(false);
      setCloudProjectId(null);
      setCloudVersionId(null);
      setCloudProjectName(null);
      setCloudProjectAccessRole(null);
      setCloudShareToken(null);
      setCloudVersionConflict(false);
      await fileActions.createNewProject("New Project", false);
      // Scene is cleared by createNewProject - don't open Upload Manager panel
    } catch (error) {
      logger.error("Error creating new project:", error);
    }
  }, [fileActions, rememberCurrentSceneForRestore]);

  React.useEffect(() => {
    window.addEventListener(SHARED_PROJECT_NEW_PROJECT_EVENT, handleNewProject);
    return () => {
      window.removeEventListener(SHARED_PROJECT_NEW_PROJECT_EVENT, handleNewProject);
    };
  }, [handleNewProject]);

  React.useEffect(() => {
    const openCloudProjects = () => setCloudProjectsOpen(true);
    window.addEventListener(CLOUD_PROJECT_VERSION_CONFLICT_EVENT, openCloudProjects);
    return () => {
      window.removeEventListener(CLOUD_PROJECT_VERSION_CONFLICT_EVENT, openCloudProjects);
    };
  }, []);

  const handleSceneFilesSelected = React.useCallback(
    async (event) => {
      const files = Array.from(event.target.files ?? []);
      const preparedFiles = await prepareFilesForUrdfImport(files);
      if (!preparedFiles) {
        event.target.value = "";
        return;
      }
      const replacesCurrentScene = preparedFiles.some(isXmlFile) && !isResolvingMissingUpload();
      if (replacesCurrentScene) {
        const confirmed = await requestSceneReplacementConfirmation({
          hasFileStoreWork: hasFileStoreSceneWork(),
          currentSnapshotHash: getCurrentProjectSaveSnapshotHash(),
        });
        if (!confirmed) {
          event.target.value = "";
          return;
        }
        rememberCurrentSceneForRestore();
        setReadOnlyProjectMode(false);
        setCloudProjectId(null);
        setCloudVersionId(null);
        setCloudProjectName(null);
        setCloudProjectAccessRole(null);
        setCloudShareToken(null);
        setCloudVersionConflict(false);
      }

      await fileActions.handleFiles({
        target: {
          files: preparedFiles,
          value: "",
        },
      });
      event.target.value = "";
    },
    [fileActions, rememberCurrentSceneForRestore]
  );

  const handleEnvironmentSelected = React.useCallback(async (file) => {
    const confirmed = await requestSceneReplacementConfirmation({
      hasFileStoreWork: hasFileStoreSceneWork(),
      currentSnapshotHash: getCurrentProjectSaveSnapshotHash(),
    });
    if (!confirmed) return;

    rememberCurrentSceneForRestore();
    setReadOnlyProjectMode(false);
    setCloudProjectId(null);
    setCloudVersionId(null);
    setCloudProjectName(null);
    setCloudProjectAccessRole(null);
    setCloudShareToken(null);
    setCloudVersionConflict(false);
    await loadEnvironmentZip(file);
  }, [rememberCurrentSceneForRestore]);

  const handleSaveCloudVersion = React.useCallback(
    async ({
      kind,
      label = null,
      projectName = null,
      showProgress = true,
      forceNewProject = false,
    }) => {
      if (cloudActionPending || cloudAutosavePendingRef.current) return false;
      const isAutosave = kind === "autosave";
      if (isReadOnlyProjectMode() || (cloudProjectId && cloudProjectAccessRole === "viewer")) {
        if (!isAutosave) {
          showMainPanelToast("This shared project is view-only.");
        }
        return false;
      }
      const saveActions = getProjectSaveActions();
      if (isAutosave) {
        cloudAutosavePendingRef.current = true;
      } else {
        setCloudActionPending(true);
      }
      if (showProgress) {
        setCloudProgressLabel("cloud project");
        setCloudProgressVerb("Saving");
        setCloudProgressOpen(true);
        setCloudProgressPct(8);
      }
      if (!isAutosave) saveActions.markProjectSaving("cloud");
      try {
        if (showProgress) setCloudProgressPct(22);
        const targetCloudProjectId = forceNewProject ? null : cloudProjectId;
        const targetCloudVersionId = forceNewProject ? null : cloudVersionId;
        const targetCloudShareToken = forceNewProject ? null : cloudShareToken;
        const targetCloudAccessRole = forceNewProject ? null : cloudProjectAccessRole;
        const requestedProjectName =
          projectName ||
          (forceNewProject ? null : cloudProjectName) ||
          exportProjectName ||
          useFileStore.getState().projectName;
        const result = await syncProjectToCloud(
          {
            cloudProjectId: targetCloudProjectId,
            name: requestedProjectName,
          },
          {
            kind,
            label,
            baseVersionId: targetCloudProjectId ? targetCloudVersionId : null,
            shareToken: targetCloudAccessRole === "editor" ? targetCloudShareToken : null,
            onDiagnostics: () => {
              if (showProgress) setCloudProgressPct(82);
            },
          }
        );
        if (showProgress) setCloudProgressPct(96);
        setCloudProjectId(result.projectId);
        setCloudVersionId(result.versionId);
        setCloudVersionConflict(false);
        setCloudProjectAccessRole(
          targetCloudShareToken
            ? "editor"
            : targetCloudProjectId
              ? (targetCloudAccessRole ?? "owner")
              : "owner"
        );
        if (forceNewProject) setCloudShareToken(null);
        setReadOnlyProjectMode(false);
        if (!isAutosave && projectName?.trim()) {
          await patchProject(result.projectId, { name: projectName.trim() });
        }
        setCloudProjectName((currentName) =>
          !isAutosave && projectName?.trim()
            ? projectName.trim()
            : (currentName ?? requestedProjectName ?? null)
        );
        setCloudProjectsRefreshKey((key) => key + 1);
        saveActions.markProjectSaved(getCurrentProjectSaveSnapshotHash(), "cloud");
        cloudAutosaveDirtySinceRef.current = null;
        cloudAutosaveLastCloudSavedAtRef.current = Date.now();
        if (showProgress) setCloudProgressPct(100);
        if (!isAutosave) {
          showMainPanelToast("Cloud save complete.", { icon: "cloud-check" });
        }
        if (showProgress) await delay(250);
        return true;
      } catch (error) {
        if (isCloudVersionConflictError(error)) {
          setCloudVersionConflict(true);
          if (isAutosave) {
            logger.warn("[main] cloud autosave paused after version conflict:", error);
          } else {
            saveActions.markProjectSaveError(error);
            logger.warn("[main] cloud save blocked by version conflict:", error);
          }
          showMainPanelToast(cloudErrorMessage(error), {
            tone: "warning",
            action: { label: "Open Cloud Projects", event: CLOUD_PROJECT_VERSION_CONFLICT_EVENT },
            durationMs: 10000,
          });
          return false;
        }
        if (isAutosave) {
          logger.warn("[main] cloud autosave failed:", error);
        } else {
          saveActions.markProjectSaveError(error);
          logger.error("[main] failed to save project to cloud:", error);
          if (!showBrowserImportLimitIssue(error)) {
            showMainPanelToast(cloudErrorMessage(error));
          }
        }
        return false;
      } finally {
        if (isAutosave) {
          cloudAutosavePendingRef.current = false;
        } else {
          setCloudActionPending(false);
        }
        if (showProgress) {
          setCloudProgressOpen(false);
          setCloudProgressPct(0);
        }
      }
    },
    [
      cloudActionPending,
      cloudProjectAccessRole,
      cloudProjectId,
      cloudProjectName,
      cloudVersionId,
      cloudShareToken,
      exportProjectName,
    ]
  );

  const handleSaveCloudNamed = React.useCallback(
    async (suggestedName) => {
      const trimmedName = String(
        suggestedName || cloudProjectName || exportProjectName || "Cloud save"
      ).trim();
      return handleSaveCloudVersion({
        kind: "named",
        label: trimmedName,
        projectName: trimmedName,
      });
    },
    [cloudProjectName, exportProjectName, handleSaveCloudVersion]
  );

  const refreshCloudAwareExportProjectName = React.useCallback(() => {
    if (cloudProjectName?.trim()) {
      setExportProjectName(cloudProjectName.trim());
      return;
    }
    refreshSuggestedExportProjectName();
  }, [cloudProjectName, refreshSuggestedExportProjectName, setExportProjectName]);

  React.useEffect(() => {
    const onProjectSaveRequest = (event) => {
      const detail = event.detail;
      if (
        !detail ||
        (detail.target !== "cloud" && detail.target !== "cloud-new") ||
        typeof detail.resolve !== "function"
      )
        return;
      detail.handled = true;
      const forceNewProject = detail.target === "cloud-new";
      const name = forceNewProject ? exportProjectName : cloudProjectName || exportProjectName;
      void handleSaveCloudVersion({
        kind: "named",
        label: String(name || "Cloud save").trim(),
        projectName: String(name || "Cloud save").trim(),
        forceNewProject,
      }).then((saved) => {
        detail.resolve(Boolean(saved));
      });
    };
    window.addEventListener(PROJECT_SAVE_REQUEST_EVENT, onProjectSaveRequest);
    return () => window.removeEventListener(PROJECT_SAVE_REQUEST_EVENT, onProjectSaveRequest);
  }, [cloudProjectName, exportProjectName, handleSaveCloudVersion]);

  React.useEffect(() => {
    if (
      !cloudAuthLoaded ||
      !cloudAuthSignedIn ||
      cloudProjectAccessRole === "viewer" ||
      cloudVersionConflict ||
      !currentProjectSaveSnapshotHash ||
      !hasMeaningfulSceneWork() ||
      cloudSaveMatchesCurrent
    ) {
      cloudAutosaveDirtySinceRef.current = null;
      return;
    }
    if (projectSaveStatus === "saving") return;
    if (cloudActionPending || cloudAutosavePendingRef.current) return;

    if (cloudAutosaveTimerRef.current) {
      window.clearTimeout(cloudAutosaveTimerRef.current);
    }

    const now = Date.now();
    cloudAutosaveDirtySinceRef.current = cloudAutosaveDirtySinceRef.current ?? now;
    const dirtyForMs = now - cloudAutosaveDirtySinceRef.current;
    const maxUnsavedDelayMs = Math.max(0, CLOUD_AUTOSAVE_MAX_UNSAVED_MS - dirtyForMs);
    const debounceDelayMs = Math.min(CLOUD_AUTOSAVE_DEBOUNCE_MS, maxUnsavedDelayMs);
    const minIntervalDelayMs = cloudAutosaveLastCloudSavedAtRef.current
      ? Math.max(
          0,
          CLOUD_AUTOSAVE_MIN_INTERVAL_MS - (now - cloudAutosaveLastCloudSavedAtRef.current)
        )
      : 0;
    const autosaveDelayMs = Math.max(debounceDelayMs, minIntervalDelayMs);

    cloudAutosaveTimerRef.current = window.setTimeout(() => {
      cloudAutosaveTimerRef.current = null;
      if (cloudActionPending || cloudAutosavePendingRef.current) return;
      void handleSaveCloudVersion({
        kind: "autosave",
        label: null,
        projectName: autosaveProjectName(exportProjectName, projectSaveFileState.projectName),
        showProgress: false,
      });
    }, autosaveDelayMs);

    return () => {
      if (cloudAutosaveTimerRef.current) {
        window.clearTimeout(cloudAutosaveTimerRef.current);
        cloudAutosaveTimerRef.current = null;
      }
    };
  }, [
    cloudActionPending,
    cloudAuthLoaded,
    cloudAuthSignedIn,
    cloudProjectAccessRole,
    cloudVersionConflict,
    cloudSaveMatchesCurrent,
    currentProjectSaveSnapshotHash,
    exportProjectName,
    handleSaveCloudVersion,
    projectSaveFileState.projectName,
    projectSaveStatus,
  ]);

  const handleLoadCloudVersion = React.useCallback(
    async (projectId, versionId, project = null) => {
      const confirmed = await requestSceneReplacementConfirmation({
        hasFileStoreWork: hasFileStoreSceneWork(),
        currentSnapshotHash: getCurrentProjectSaveSnapshotHash(),
      });
      if (!confirmed) return;

      rememberCurrentSceneForRestore();
      setCloudProgressLabel("cloud project");
      setCloudProgressVerb("Opening");
      setCloudProgressOpen(true);
      setCloudProgressPct(8);
      try {
        setCloudProgressPct(28);
        setReadOnlyProjectMode(false);
        await loadProjectFromCloud(projectId, versionId);
        setCloudProgressPct(88);
        setCloudProjectId(projectId);
        setCloudVersionId(versionId || null);
        setCloudProjectName(project?.name || null);
        if (project?.name) setExportProjectName(project.name);
        const accessRole = project?.accessRole ?? (project?.isOwner === false ? "viewer" : "owner");
        setCloudProjectAccessRole(accessRole);
        setCloudShareToken(null);
        setCloudVersionConflict(false);
        setReadOnlyProjectMode(accessRole === "viewer");
        getProjectSaveActions().markProjectSaved(getCurrentProjectSaveSnapshotHash(), "cloud");
        setCloudProgressPct(100);
        await delay(250);
      } catch (error) {
        logger.error("[main] failed to open cloud project:", error);
        if (!showBrowserImportLimitIssue(error)) {
          showMainPanelToast(cloudErrorMessage(error));
        }
      } finally {
        setCloudProgressOpen(false);
        setCloudProgressPct(0);
      }
    },
    [rememberCurrentSceneForRestore, setExportProjectName]
  );

  React.useEffect(() => {
    const token = shareTokenFromLocation();
    if (!token || sharedProjectLoadStartedRef.current) return;
    sharedProjectLoadStartedRef.current = true;
    let cancelled = false;

    const loadSharedProject = async () => {
      rememberCurrentSceneForRestore();
      setCloudProgressLabel("shared project");
      setCloudProgressVerb("Opening");
      setCloudProgressOpen(true);
      setCloudProgressPct(8);
      try {
        setReadOnlyProjectMode(false);
        setCloudProgressPct(22);
        const publicInfo = await getPublicShareLink(token);
        if (cancelled) return;
        setCloudProgressPct(36);
        const detail = await getPublicShareLinkVersionDetail(token);
        if (cancelled) return;
        setCloudProgressPct(68);
        await loadProjectFromVersionDetail(detail, publicInfo.project.id);
        if (cancelled) return;
        setCloudProgressPct(92);
        setCloudProjectId(publicInfo.project.id);
        setCloudVersionId(detail.version.id);
        setCloudProjectName(publicInfo.project.name || null);
        const accessRole = publicInfo.accessRole === "editor" ? "editor" : "viewer";
        setCloudProjectAccessRole(accessRole);
        setCloudShareToken(accessRole === "editor" ? token : null);
        setCloudVersionConflict(false);
        setReadOnlyProjectMode(accessRole !== "editor");
        if (publicInfo.project.name) setExportProjectName(publicInfo.project.name);
        getProjectSaveActions().markProjectSaved(getCurrentProjectSaveSnapshotHash(), "cloud");
        setCloudProgressPct(100);
        if (accessRole === "editor") {
          showMainPanelToast("Editable shared project opened.", {
            durationMs: 7000,
          });
        } else {
          showMainPanelToast(
            "Read-only shared project opened. Create a new project to start editing.",
            {
              action: {
                label: "New Project",
                event: SHARED_PROJECT_NEW_PROJECT_EVENT,
              },
              durationMs: 10000,
            }
          );
        }
        await delay(250);
      } catch (error) {
        logger.error("[main] failed to open shared cloud project:", error);
        showMainPanelToast(cloudErrorMessage(error));
      } finally {
        if (!cancelled) {
          setCloudProgressOpen(false);
          setCloudProgressPct(0);
        }
      }
    };

    void loadSharedProject();
    return () => {
      cancelled = true;
    };
  }, [rememberCurrentSceneForRestore, setExportProjectName]);

  const handleNewProjectShortcut = React.useCallback(() => {
    void handleNewProject();
    return true;
  }, [handleNewProject]);

  const newProjectShortcutOptions = React.useMemo(
    () => ({
      preventDefault: true,
    }),
    []
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.PROJECT_NEW,
    handleNewProjectShortcut,
    newProjectShortcutOptions
  );

  // History (menu disabled state subscribes inside `MainMenuDropdown`; keep handlers free of store hooks here)
  const handleUndo = React.useCallback(() => {
    if (isPhysicsAuthoringLocked()) return;
    if (!getHistoryActions().canUndo()) return;
    getHistoryActions().undo(
      () => makeHistorySnapshot(getSceneState()),
      (snapshot) => {
        const robotActions = getSceneActions();
        robotActions.restoreState(snapshot.scene);
        useFileStore.setState({ keyframes: snapshot.keyframes || {} });
      }
    );
  }, []);

  const handleRedo = React.useCallback(() => {
    if (isPhysicsAuthoringLocked()) return;
    if (!getHistoryActions().canRedo()) return;
    getHistoryActions().redo(
      () => makeHistorySnapshot(getSceneState()),
      (snapshot) => {
        const robotActions = getSceneActions();
        robotActions.restoreState(snapshot.scene);
        useFileStore.setState({ keyframes: snapshot.keyframes || {} });
      }
    );
  }, []);

  const handlePoseEditorCreateKeyframe = async (keyframeName, ownerWorldBodyUuid) => {
    if (isPhysicsAuthoringLocked()) return;
    getHistoryActions().save(getSceneState());
    await fileActions.createKeyframe(keyframeName, ownerWorldBodyUuid);
  };

  const handlePoseEditorDeleteKeyframe = async (keyframeName) => {
    if (isPhysicsAuthoringLocked()) return;
    getHistoryActions().save(getSceneState());
    await fileActions.deleteKeyframe(keyframeName);
  };

  const handlePhysicsStartStopShortcut = React.useCallback(async () => {
    try {
      if (usePhysicsStore.getState().isRunning) await stopSimulation();
      else await startSimulationFromCurrentFiles();
    } catch {
      logger.error("Failed to toggle physics start/stop");
    }
  }, []);

  const handlePhysicsPauseResumeShortcut = React.useCallback(() => {
    const { isRunning, isPaused, actions } = usePhysicsStore.getState();
    if (!isRunning && !isPaused) return;
    if (isPaused) actions.resume();
    else actions.pause();
  }, []);

  const handlePhysicsPlayPauseShortcut = React.useCallback(
    (event) => {
      if (isInteractiveShortcutTarget(event.target)) return false;
      const { isRunning, isPaused } = usePhysicsStore.getState();
      if (isRunning || isPaused) {
        handlePhysicsPauseResumeShortcut();
      } else {
        void handlePhysicsStartStopShortcut();
      }
      return true;
    },
    [handlePhysicsPauseResumeShortcut, handlePhysicsStartStopShortcut]
  );

  const openFileUploadDialog = React.useCallback(() => {
    if (isPhysicsAuthoringLocked()) return;
    document.getElementById("file-upload")?.click();
  }, []);
  const openFolderUploadDialog = React.useCallback(() => {
    if (isPhysicsAuthoringLocked()) return;
    document.getElementById("folder-upload")?.click();
  }, []);
  const openEnvironmentLoadDialog = React.useCallback(() => {
    if (isPhysicsAuthoringLocked()) return;
    document.getElementById("environment-load")?.click();
  }, []);

  const mainShortcutOptions = React.useMemo(
    () => ({
      preventDefault: true,
    }),
    []
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.PROJECT_UPLOAD_FILES,
    () => {
      openFileUploadDialog();
      return true;
    },
    mainShortcutOptions
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.PROJECT_SAVE_ENVIRONMENT,
    () => {
      void handleExportEnvironmentZip();
      return true;
    },
    mainShortcutOptions
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.PHYSICS_PLAY_PAUSE,
    handlePhysicsPlayPauseShortcut,
    mainShortcutOptions
  );

  const handleSceneGraphShortcut = React.useCallback(
    (event) => {
      if (isInteractiveShortcutTarget(event.target)) return false;
      setSceneGraphOpenFromMenu(!usePanelStore.getState().sceneGraphOpen);
      return true;
    },
    [setSceneGraphOpenFromMenu]
  );

  const handleAssetLibraryShortcut = React.useCallback(
    (event) => {
      if (isInteractiveShortcutTarget(event.target)) return false;
      setObjectLibraryOpenFromMenu(!usePanelStore.getState().objectLibraryPanelOpen);
      return true;
    },
    [setObjectLibraryOpenFromMenu]
  );
  const handleOpenAssetLibraryFromSceneGraph = React.useCallback(() => {
    setObjectLibraryOpenFromMenu(true);
  }, [setObjectLibraryOpenFromMenu]);

  useShortcutAction(
    SHORTCUT_ACTIONS.PANEL_TOGGLE_SCENE_GRAPH,
    handleSceneGraphShortcut,
    mainShortcutOptions
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.PANEL_TOGGLE_ASSET_LIBRARY,
    handleAssetLibraryShortcut,
    mainShortcutOptions
  );

  const handleKeyboardShortcutsPanelShortcut = React.useCallback(
    (event) => {
      if (!shortcutsPanelOpen && isInteractiveShortcutTarget(event.target)) return false;
      if (shortcutsPanelOpen) {
        closeShortcutsPanel();
        return true;
      }
      setShortcutsPanelOpenFromMenu(true);
      return true;
    },
    [shortcutsPanelOpen, closeShortcutsPanel, setShortcutsPanelOpenFromMenu]
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.PANEL_TOGGLE_KEYBOARD_SHORTCUTS,
    handleKeyboardShortcutsPanelShortcut,
    mainShortcutOptions
  );

  const anyMainPanelPopupOpen =
    sceneGraphOpen ||
    filePanelOpen ||
    animationPanelOpen ||
    materialEditorPanelOpen ||
    lightsPanelOpen ||
    circleDetectionOpen ||
    historyPanelOpen ||
    runtimeDebugOpen ||
    measurementsOpen ||
    ikTargetPanelOpen ||
    objectLibraryOpen ||
    runtimeControlsOpen ||
    physicsPanelOpen ||
    actuatorControlsOpen ||
    warmstartDatasetOpen ||
    behaviorEditorOpen ||
    policyRolloutOpen ||
    trainingMetricsOpen ||
    trainingPipelineOpen ||
    octoTrainingOpen ||
    meshEditorOpen ||
    sceneInventoryOpen ||
    modelCheckOpen ||
    massPropertiesOpen ||
    terrainPanelOpen ||
    xmlSourcePanelOpen ||
    cameraProbeOpen ||
    sensorsOpen ||
    shortcutsPanelOpen ||
    getUpdatesPanelOpen ||
    sendFeedbackPanelOpen;
  const anyCloudPanelOpen = cloudProjectsOpen;
  const mobilePanelCoversMenu =
    isMobilePanelViewport && (anyMainPanelPopupOpen || anyCloudPanelOpen || propertiesPanelOpen);
  const mobileEdgeChromeSuppressedForPinnedTab = useMobileEdgeChromeSuppressed();

  useMainPanelEventSubscriptions({
    isMobilePanelViewport,
    isCollapsed,
    anyMainPanelPopupOpen: anyMainPanelPopupOpen || anyCloudPanelOpen,
    mobilePanelCoversMenu,
    closeMainPanelPopups,
    setIsCollapsed,
    setSceneGraphOpen,
    setSceneGraphOpenFromMenu,
    setPropertiesPanelOpen,
    selectTendon,
    selectEquality,
    setLastSelectionSource,
    setCameraProbeOpenFromMenu,
    setSensorsOpenFromMenu,
  });

  const { pinnedPanelId, pinnedToolPanelTabConfig } = usePinnedToolPanelTabConfig({
    isMobilePanelViewport,
    showDevTools,
    showTrainingIkTools,
    sceneGraphOpen,
    setSceneGraphOpenFromMenu,
    closeSceneGraph,
    sceneInventoryOpen,
    setSceneInventoryOpenFromMenu,
    closeSceneInventory,
    modelCheckOpen,
    setModelCheckOpenFromMenu,
    closeModelCheckPanel,
    massPropertiesOpen,
    setMassPropertiesOpenFromMenu,
    closeMassPropertiesPanel,
    filePanelOpen,
    setFilePanelOpenFromMenu,
    closeFilePanel,
    animationPanelOpen,
    setAnimationPanelOpenFromMenu,
    closeAnimationPanel,
    materialEditorPanelOpen,
    setMaterialEditorPanelOpenFromMenu,
    closeMaterialEditorPanel,
    lightsPanelOpen,
    setLightsPanelOpenFromMenu,
    closeLightsPanel,
    ikTargetPanelOpen,
    setIkTargetPanelOpenFromMenu,
    closeIkTargetPanel,
    physicsPanelOpen,
    runtimeControlsOpen,
    openPhysicsPanel,
    openRuntimeControlsPanel,
    closeRuntimeControlsPanel,
    closePhysicsPanel,
    actuatorControlsOpen,
    setActuatorControlsOpenFromMenu,
    closeActuatorControlsPanel,
    warmstartDatasetOpen,
    openWarmstartDatasetPanel,
    closeWarmstartDatasetPanel,
    behaviorEditorOpen,
    setBehaviorEditorOpenFromMenu,
    closeBehaviorEditorPanel,
    trainingPipelineOpen,
    setTrainingPipelineOpenFromMenu,
    closeTrainingPipelinePanel,
    octoTrainingOpen,
    setOctoTrainingOpenFromMenu,
    closeOctoTrainingPanel,
    trainingMetricsOpen,
    setTrainingMetricsOpenFromMenu,
    closeTrainingMetricsPanel,
    policyRolloutOpen,
    openPolicyRolloutPanel,
    closePolicyRolloutPanel,
    circleDetectionOpen,
    setCircleDetectionOpenFromMenu,
    closeCircleDetectionPanel,
    historyPanelOpen,
    setHistoryPanelOpenFromMenu,
    closeHistoryPanel,
    runtimeDebugOpen,
    setRuntimeDebugOpenFromMenu,
    closeRuntimeDebugPanel,
    measurementsOpen,
    setMeasurementsOpenFromMenu,
    closeMeasurementsPanel,
    meshEditorOpen,
    setMeshEditorOpenFromMenu,
    closeMeshEditorPanel,
    xmlSourcePanelOpen,
    setXmlSourcePanelOpenFromMenu,
    closeXmlSourcePanel,
    terrainPanelOpen,
    setTerrainPanelOpenFromMenu,
    closeTerrainPanel,
    objectLibraryOpen,
    setObjectLibraryOpenFromMenu,
    closeObjectLibraryPanel,
    cameraProbeOpen,
    setCameraProbeOpenFromMenu,
    closeCameraProbePanel,
    sensorsOpen,
    setSensorsOpenFromMenu,
    closeSensorsPanel,
  });

  const mainMenuSwipeToCloseHandlers = useSwipeToClose({
    enabled: isMobilePanelViewport && !isCollapsed && !mobilePanelCoversMenu,
    onClose: handleCollapse,
  });
  const mainMenuTabSwipeToOpenHandlers = useSwipeToOpen({
    enabled: isMobilePanelViewport && isCollapsed && !mobilePanelCoversMenu,
    onOpen: handleExpand,
  });

  return (
    <>
      <MainPanelCollapsedTab
        isCollapsed={isCollapsed}
        isMobilePanelViewport={isMobilePanelViewport}
        mobilePanelCoversMenu={mobilePanelCoversMenu}
        position={position}
        swipeHandlers={mainMenuTabSwipeToOpenHandlers}
        onExpand={handleExpand}
        onNewProject={handleNewProject}
        onUploadFilesClick={openFileUploadDialog}
        onUploadFolderClick={openFolderUploadDialog}
        onExportXml={handleExportMjcfXml}
        onExportEnvironmentZip={handleExportEnvironmentZip}
        onQuickSaveEnvironment={handleQuickDownloadEnvironmentBundle}
        onSaveCloudNamed={handleSaveCloudNamed}
        onOpenCloudProjects={() => setCloudProjectsOpen(true)}
        onExportDeploymentBundle={handleExportDeploymentBundle}
        onLoadEnvironmentClick={openEnvironmentLoadDialog}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onOpenVisibilitySettings={() => setVisibilitySettingsOpenFromMenu(true)}
        onOpenShortcuts={() => setShortcutsPanelOpenFromMenu(true)}
        onOpenGetUpdates={() => setGetUpdatesPanelOpenFromMenu(true)}
        onOpenSendFeedback={() => setSendFeedbackPanelOpenFromMenu(true)}
        onOpenAbout={() => {
          setAboutPanelMode("about");
          setAboutPanelOpenFromMenu(true);
        }}
        onOpenPrivacySettings={() => {
          setAboutPanelMode("privacy");
          setAboutPanelOpenFromMenu(true);
        }}
        exportProjectName={exportProjectName}
        onExportProjectNameChange={setExportProjectName}
        onRefreshExportProjectName={refreshCloudAwareExportProjectName}
        quickProjectSavePending={quickProjectSavePending}
        cloudActionPending={cloudActionPending}
        cloudSaveMatchesCurrent={cloudSaveMatchesCurrent}
      />

      {pinnedToolPanelTabConfig && (
        <MobilePinnedToolPanelTab
          pinnedId={pinnedPanelId}
          Icon={pinnedToolPanelTabConfig.Icon}
          label={pinnedToolPanelTabConfig.label}
          isOpen={pinnedToolPanelTabConfig.isOpen}
          suppressed={mobileEdgeChromeSuppressedForPinnedTab}
          onToggle={pinnedToolPanelTabConfig.onToggle}
          onSwipeOpen={pinnedToolPanelTabConfig.onSwipeOpen}
        />
      )}

      <div
        className={`main-menu-panel fixed backdrop-blur-sm border border-gray-700 rounded-lg shadow-xl w-[calc(100vw-20px)] max-h-[calc(100vh-20px)] overflow-hidden z-20${mobilePanelCoversMenu ? " main-menu-panel-mobile-covered" : ""}`}
        {...mainMenuSwipeToCloseHandlers}
        style={{
          ...position,
          backgroundColor: "var(--panel-surface)",
          transform: isCollapsed ? "translateX(calc(-100% - 12px))" : "translateX(0)",
          transition: "transform 0.25s ease-in-out",
          width: `min(calc(100vw - 20px), ${
            isMobilePanelViewport
              ? MAIN_MENU_PANEL_MAX_WIDTH_MOBILE
              : MAIN_MENU_PANEL_MAX_WIDTH_DEFAULT
          })`,
          maxWidth: isMobilePanelViewport
            ? MAIN_MENU_PANEL_MAX_WIDTH_MOBILE
            : MAIN_MENU_PANEL_MAX_WIDTH_DEFAULT,
        }}
      >
        <MainPanelHeader
          onCollapse={handleCollapse}
          onNewProject={handleNewProject}
          onUploadFilesClick={openFileUploadDialog}
          onUploadFolderClick={openFolderUploadDialog}
          onExportXml={handleExportMjcfXml}
          onExportEnvironmentZip={handleExportEnvironmentZip}
          onQuickSaveEnvironment={handleQuickDownloadEnvironmentBundle}
          onSaveCloudNamed={handleSaveCloudNamed}
          onOpenCloudProjects={() => setCloudProjectsOpen(true)}
          onExportDeploymentBundle={handleExportDeploymentBundle}
          onLoadEnvironmentClick={openEnvironmentLoadDialog}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onOpenVisibilitySettings={() => setVisibilitySettingsOpenFromMenu(true)}
          onOpenShortcuts={() => setShortcutsPanelOpenFromMenu(true)}
          onOpenGetUpdates={() => setGetUpdatesPanelOpenFromMenu(true)}
          onOpenSendFeedback={() => setSendFeedbackPanelOpenFromMenu(true)}
          onOpenAbout={() => {
            setAboutPanelMode("about");
            setAboutPanelOpenFromMenu(true);
          }}
          onOpenPrivacySettings={() => {
            setAboutPanelMode("privacy");
            setAboutPanelOpenFromMenu(true);
          }}
          exportProjectName={exportProjectName}
          onExportProjectNameChange={setExportProjectName}
          onRefreshExportProjectName={refreshCloudAwareExportProjectName}
          quickProjectSavePending={quickProjectSavePending}
          cloudActionPending={cloudActionPending}
          cloudSaveMatchesCurrent={cloudSaveMatchesCurrent}
        />

        <MainPanelHiddenFileInputs
          onFilesSelected={handleSceneFilesSelected}
          onEnvironmentSelected={handleEnvironmentSelected}
        />

        <MainPanelContent
          isMobilePanelViewport={isMobilePanelViewport}
          showDevTools={showDevTools}
          showRuntimeDiagnostics={showRuntimeDiagnostics}
          showTrainingIkTools={showTrainingIkTools}
          showPerformancePanel={showPerformancePanel}
          onTogglePerformancePanel={toggleShowPerformancePanel}
          onStartStop={handlePhysicsStartStopShortcut}
          onPauseResume={handlePhysicsPauseResumeShortcut}
          onOpenRuntimeControlsPanel={openRuntimeControlsPanel}
          onOpenPhysicsPanel={openPhysicsPanel}
          getPanelToggleButtonClass={getPanelToggleButtonClass}
          closeMainPanelPopups={closeMainPanelPopups}
        />
      </div>

      {isMobilePanelViewport && (
        <MainPanelMobilePhysicsOverlay
          actuatorControlsOpen={actuatorControlsOpen}
          onStartStop={handlePhysicsStartStopShortcut}
          onPauseResume={handlePhysicsPauseResumeShortcut}
          isModelCheckOpen={modelCheckOpen}
          onOpenModelCheckPanel={() => setModelCheckOpenFromMenu(true)}
          onOpenRuntimeControlsPanel={openRuntimeControlsPanel}
          onOpenPhysicsPanel={openPhysicsPanel}
        />
      )}

      <MainPanelToolPanels
        handleBodyReorder={handleBodyReorder}
        handleOpenAssetLibraryFromSceneGraph={handleOpenAssetLibraryFromSceneGraph}
        handleFiles={handleSceneFilesSelected}
        handlePoseEditorCreateKeyframe={handlePoseEditorCreateKeyframe}
        handlePoseEditorDeleteKeyframe={handlePoseEditorDeleteKeyframe}
        motorGain={motorGain}
        setMotorGain={setMotorGain}
        showDevTools={showDevTools}
        showTrainingIkTools={showTrainingIkTools}
        openWarmstartDatasetPanel={openWarmstartDatasetPanel}
        openTrainingMetricsPanel={openTrainingMetricsPanel}
        openPolicyRolloutPanel={openPolicyRolloutPanel}
        openOctoTrainingPanel={openOctoTrainingPanel}
        policyRolloutRequest={policyRolloutRequest}
        loadingXmlSourceId={loadingXmlSourceId}
        selectedXmlSourceId={selectedXmlSourceId}
        handleXmlSourceSelect={handleXmlSourceSelect}
        aboutPanelMode={aboutPanelMode}
      />
      {cloudProjectsOpen && (
        <CloudProjectsPanel
          open={cloudProjectsOpen}
          refreshKey={cloudProjectsRefreshKey}
          currentProjectId={cloudProjectId}
          currentVersionId={cloudVersionId}
          currentProjectName={cloudProjectName}
          cloudActionPending={cloudActionPending}
          onClose={() => setCloudProjectsOpen(false)}
          onLoadVersion={handleLoadCloudVersion}
          onSaveCheckpoint={handleSaveCloudNamed}
          onCurrentProjectRenamed={(name) => {
            setCloudProjectName(name || null);
            if (name) setExportProjectName(name);
          }}
          onCurrentProjectDeleted={() => {
            setCloudProjectId(null);
            setCloudVersionId(null);
            setCloudProjectName(null);
            setCloudProjectAccessRole(null);
            setCloudShareToken(null);
            setCloudVersionConflict(false);
            getProjectSaveActions().markProjectDirty();
            showMainPanelToast(
              "Deleted the current cloud project. Your open scene is unchanged; save to create a new cloud project.",
              { icon: "cloud" }
            );
          }}
          onCurrentVersionDeleted={() => {
            setCloudVersionId(null);
            getProjectSaveActions().markProjectDirty();
            showMainPanelToast(
              "Deleted the current saved version. Your open scene is unchanged; save to create a new version.",
              { icon: "cloud" }
            );
          }}
        />
      )}
      <MobileSceneLoadOverlay
        open={isMobilePanelViewport && cloudProgressOpen}
        mobileLoadItemLabel={cloudProgressLabel}
        mobileLoadProgressPct={cloudProgressPct}
        loadVerb={cloudProgressVerb}
        anyLibraryCatalogInflight={false}
        onCancelCatalogLoad={undefined}
      />
      <DesktopSceneLoadToast
        open={!isMobilePanelViewport && cloudProgressOpen}
        loadItemLabel={cloudProgressLabel}
        loadProgressPct={cloudProgressPct}
        loadVerb={cloudProgressVerb}
        anyLibraryCatalogInflight={false}
        onCancelCatalogLoad={undefined}
      />
      <MainPanelExportModals
        pendingKeyframeMismatchExport={pendingKeyframeMismatchExport}
        handleSkipMismatchedKeyframes={handleSkipMismatchedKeyframes}
        handleAbortMismatchedKeyframes={handleAbortMismatchedKeyframes}
        pendingMjcfExport={pendingMjcfExport}
        exportProjectName={exportProjectName}
        setExportProjectName={setExportProjectName}
        includeMjcfDesktopScene={includeMjcfDesktopScene}
        setIncludeMjcfDesktopScene={setIncludeMjcfDesktopScene}
        setPendingMjcfLiftModelToFloor={setPendingMjcfLiftModelToFloor}
        handleDownloadPendingMjcfExport={handleDownloadPendingMjcfExport}
        handleExportReviewFeedback={handleExportReviewFeedback}
        handleExportReviewUpdates={handleExportReviewUpdates}
        handleDownloadPendingMjcfBundle={handleDownloadPendingMjcfBundle}
        handleCancelPendingMjcfExport={handleCancelPendingMjcfExport}
        pendingEnvironmentExport={pendingEnvironmentExport}
        handleDownloadPendingEnvironmentLightBundle={handleDownloadPendingEnvironmentLightBundle}
        handleDownloadPendingEnvironmentBundle={handleDownloadPendingEnvironmentBundle}
        handleCancelPendingEnvironmentExport={handleCancelPendingEnvironmentExport}
        pendingDeploymentBundle={pendingDeploymentBundle}
        handleDownloadPendingDeploymentBundle={handleDownloadPendingDeploymentBundle}
        handleCancelPendingDeploymentBundle={handleCancelPendingDeploymentBundle}
      />
    </>
  );
};

export default React.memo(ModernMainPanel);
